import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, List, Skeleton, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import { request } from '@/request';
import { t } from '@/utils/language';
import { useLanguage } from '@/context/lang/languageContext';

const { Text } = Typography;

const PRIORITY_COLORS = {
  HIGH: '#ff4d4f',
  MEDIUM: '#faad14',
  LOW: '#1890ff',
};

const DEFAULT_LIMIT = 10;
const MAX_DISPLAY_ITEMS = 5;

const NotificationPopover = ({ open, onViewAll }) => {
  const { currentLang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const priorityLabelMap = useMemo(
    () => ({
      HIGH: t('notifications.priority.high'),
      MEDIUM: t('notifications.priority.medium'),
      LOW: t('notifications.priority.low'),
    }),
    [currentLang]
  );

  const typeLabelMap = useMemo(
    () => ({
      GENERAL: t('notifications.type.general'),
      SYSTEM: t('notifications.type.system'),
      ALERT: t('notifications.type.alert'),
    }),
    [currentLang]
  );

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await request.list({
        listURL: 'get_notifications',
        options: {
          intPageCount: 1,
          intLimit: DEFAULT_LIMIT,
          isUnread: true,
        },
      });

      if (response?.success) {
        setNotifications(response?.arrList || []);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Failed to fetch unread notifications', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const renderNotification = (item) => {
    const priority = (item?.strPriority || '').toUpperCase();
    const createdAt = item?.strCreatedTime
      ? dayjs(item.strCreatedTime).format('YYYY-MM-DD HH:mm')
      : '';

    return (
      <List.Item key={item?._id || Math.random().toString(36)}>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space align="start" size={8} style={{ justifyContent: 'space-between' }}>
            <Text strong ellipsis={{ tooltip: item?.strTitle }}>{item?.strTitle}</Text>
            {priority ? (
              <Tag color={PRIORITY_COLORS[priority] || 'blue'}>
                {priorityLabelMap[priority] || priority}
              </Tag>
            ) : null}
          </Space>
          <Text type="secondary">
            {item?.timeAgo || ''}
            {createdAt ? ` • ${createdAt}` : ''}
            {item?.strType ? ` • ${typeLabelMap[item.strType] || item.strType}` : ''}
          </Text>
          {item?.strDescription ? (
            <Text type="secondary" ellipsis={{ tooltip: item.strDescription }}>
              {item.strDescription}
            </Text>
          ) : null}
        </Space>
      </List.Item>
    );
  };

  return (
    <div className="notification-popover">
      {loading ? (
        <div style={{ width: 320 }}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      ) : notifications.length === 0 ? (
        <Empty description={t('notifications.drawer.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={notifications.slice(0, MAX_DISPLAY_ITEMS)}
          renderItem={renderNotification}
          style={{ width: 360 }}
          itemLayout="vertical"
        />
      )}
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Button type="link" onClick={onViewAll}>
          {t('navigation.notificationCenter')}
        </Button>
      </div>
    </div>
  );
};

export default NotificationPopover;
